import React, { createContext, useContext, useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { API_BASE } from '../config';

interface SignalRContextType {
    connection: signalR.HubConnection | null;
    isConnected: boolean;
}

const SignalRContext = createContext<SignalRContextType>({
    connection: null,
    isConnected: false,
});

export const useSignalR = () => useContext(SignalRContext);

export const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    useEffect(() => {
        // Extract base URL from API_BASE (remove /api/v1)
        // Assuming API_BASE is something like "https://localhost:7001/api/v1"
        // We want "https://localhost:7001/gameHub"
        const baseUrl = API_BASE.replace('/api/v1', '');
        const hubUrl = `${baseUrl}/gameHub`;

        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Information)
            .build();

        setConnection(newConnection);
    }, []);

    useEffect(() => {
        if (connection) {
            connection.start()
                .then(() => {
                    console.log('SignalR Connected');
                    setIsConnected(true);
                })
                .catch(err => console.error('SignalR Connection Error: ', err));

            connection.onclose(() => {
                setIsConnected(false);
                console.log('SignalR Disconnected');
            });

            connection.onreconnecting(() => {
                setIsConnected(false);
                console.log('SignalR Reconnecting...');
            });

            connection.onreconnected(() => {
                setIsConnected(true);
                console.log('SignalR Reconnected');
            });
        }
    }, [connection]);

    return (
        <SignalRContext.Provider value={{ connection, isConnected }}>
            {children}
        </SignalRContext.Provider>
    );
};
